import { format } from 'date-fns-tz';
import { Periods } from '@/creditflowresearch/ui/common/constants';
import {
  FULL_DATETIME_WITH_TIMEZONE_FORMAT,
  MONTH_YEAR_FORMAT,
  QUARTER_YEAR_FORMAT,
  YEAR_FORMAT,
} from '@dealroadshow/uikit-date-time/dateFormats';
import { isScreenXXs } from '@creditflowresearch/uilib/utils/screen';

const MAP_FORMATS = {
  [Periods.YEAR]: YEAR_FORMAT,
  [Periods.QUARTER]: QUARTER_YEAR_FORMAT,
  [Periods.MONTH]: MONTH_YEAR_FORMAT,
};

export const getDateLabelByPeriod = (timestamp: number, period: Periods) => {
  const dateFormat = MAP_FORMATS[period] || FULL_DATETIME_WITH_TIMEZONE_FORMAT;

  try {
    return format(timestamp, dateFormat, { timeZone: 'UTC' });
  } catch (error) {
    return timestamp;
  }
};

export const setArrowsPositions = (navigatorElement: HTMLElement) => {
  if (isScreenXXs()) {
    return;
  }

  const leftArrow = navigatorElement?.querySelector('.highcharts-navigator-handle-left');
  const rightArrow = navigatorElement?.querySelector('.highcharts-navigator-handle-right');

  if (rightArrow && leftArrow) {
    leftArrow.setAttribute('y', '19');
    leftArrow.setAttribute('x', '-3.5');
    rightArrow.setAttribute('y', '19');
    rightArrow.setAttribute('x', '-4.5');
  }
};

/**
 * Draw multi-line legend items centered - by setting offset by X
 */
export const centerLegendItems = (chart) => {
  const { legend } = chart;

  let lines = [];
  let index = 0;
  let currentDiffX = 0;
  let positionsY = [];
  let currentPositionY = 0;
  let { legendWidth, options: { itemDistance } } = legend;

  legend.allItems.forEach((item) => {
    const legendGroup = item.legendItem.group;

    // remember all the Y coordinates of rows
    if (currentPositionY !== legendGroup.translateY) {
      currentPositionY = legendGroup.translateY;
      positionsY.push(currentPositionY);
    }

    // create new lines depending on the width of the rows and do left offset
    if (lines[index] && (lines[index].width + item.itemWidth) <= legendWidth) {
      lines[index].items.push(item);
      legendGroup.attr({ translateX: currentDiffX });
      lines[index].width += item.itemWidth;
      currentDiffX += item.itemWidth;
    } else {
      index = lines.push({ items: [item], width: item.itemWidth - itemDistance }) - 1;
      legendGroup.attr({ translateX: 0 });
      currentDiffX = item.itemWidth;
    }
  });

  // do center offset
  lines.forEach((line, lineIndex) => {
    let offset = (legendWidth - line.width) / 2;
    line.items.forEach((item) => {
      const legendGroup = item.legendItem.group;

      legendGroup.attr({
        translateX: offset + legendGroup.translateX,
        translateY: positionsY[lineIndex],
      });
    });
  });
};

/**
 * Adjust chart size on small devices, if legend is multi-line and bigger than allowed value
 * As we turned off legend.navigation arrows - large multi-line legends make chart too small
 */
export const adjustChartToLegendHeight = (chart, defaultChartHeight, allowedLegendHeight) => {
  const { legend } = chart;
  let height = defaultChartHeight;

  if (legend.legendHeight > allowedLegendHeight) {
    height = defaultChartHeight + (legend.legendHeight - allowedLegendHeight);
  }

  if (chart.chartHeight !== height) {
    chart.setSize(null, `${ height }px`);
  }
};

export const calculateTimeIntervalByDay = (countDays: number) => 1000 * 3600 * 24 * countDays;
